<!--
 * @Description: 周期巡检统计清单
-->
<template>
  <scroll-layout class="inspection-statistics-list">
    <template slot="header">
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>
    <ykc-table
      ref="YkcTable"
      operateFixedType="right"
      :data="tableData"
      title="周期巡检统计清单"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons">
      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>

    <!-- 查看弹窗 -->
    <el-dialog
      title="巡检统计清单详情"
      :visible.sync="detailVisible"
      width="960px"
      :append-to-body="true"
      custom-class="inspection-statistics-detail-dialog">
      <div v-if="detailLoading" class="detail-loading">
        <i class="el-icon-loading"></i>
        加载中...
      </div>
      <div v-else-if="detailData" class="detail-content">
        <!-- 基础信息 -->
        <div class="detail-section">
          <div class="detail-section-title">基础信息</div>
          <div class="detail-info-grid">
            <div class="detail-info-item">
              <span class="detail-info-label">清单编码</span>
              <span class="detail-info-value">{{ detailData.listCode || '--' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">清单名称</span>
              <span class="detail-info-value">{{ detailData.listName || '--' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">周期开始时间</span>
              <span class="detail-info-value">{{ detailData.cycleStartDate || '--' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">周期结束时间</span>
              <span class="detail-info-value">{{ detailData.cycleEndDate || '--' }}</span>
            </div>
          </div>
        </div>

        <!-- 电站巡检统计 -->
        <div class="detail-section">
          <div class="detail-section-title">电站巡检统计</div>
          <div class="detail-toolbar">
            <el-input
              v-model="detailSearchParams.stationName"
              placeholder="请输入电站名称"
              clearable
              size="small"
              class="detail-toolbar-input"
              @keyup.enter.native="handleDetailSearch"></el-input>
            <el-button size="small" type="primary" @click="handleDetailSearch">查询</el-button>
            <el-button size="small" @click="handleDetailExport">导出Excel</el-button>
          </div>
          <el-table
            v-loading="detailTableLoading"
            :data="detailTableData"
            border
            size="small"
            class="detail-items-table">
            <el-table-column prop="stationName" label="电站名称" min-width="160">
              <template slot-scope="{ row }">{{ row.stationName || '--' }}</template>
            </el-table-column>
            <el-table-column prop="stationCode" label="电站编码" min-width="140">
              <template slot-scope="{ row }">{{ row.stationCode || '--' }}</template>
            </el-table-column>
            <el-table-column prop="platformName" label="所属平台" min-width="100" align="center">
              <template slot-scope="{ row }">{{ formatPlatform(row) }}</template>
            </el-table-column>
            <el-table-column
              prop="inspectionStatus"
              label="巡检完成情况"
              min-width="110"
              align="center">
              <template slot-scope="{ row }">
                <span :class="row.inspectionStatus === 1 ? 'status-done' : 'status-undo'">
                  {{
                    row.inspectionStatusText || (row.inspectionStatus === 1 ? '已完成' : '未完成')
                  }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="completionTime" label="完成时间" min-width="150">
              <template slot-scope="{ row }">{{ row.completionTime || '--' }}</template>
            </el-table-column>
            <el-table-column prop="responsiblePerson" label="负责人" min-width="100">
              <template slot-scope="{ row }">{{ row.responsiblePerson || '--' }}</template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="detail-pagination"
            background
            layout="total, sizes, prev, pager, next"
            :total="detailPageInfo.total"
            :current-page="detailPageInfo.current"
            :page-size="detailPageInfo.size"
            :page-sizes="[10, 20, 50]"
            @size-change="handleDetailSizeChange"
            @current-change="handleDetailCurrentChange"></el-pagination>
        </div>
      </div>
      <div v-else class="detail-empty">暂无数据</div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </scroll-layout>
</template>

<script>
  import {
    getInspectionStatisticsPage,
    getInspectionStatisticsDetail,
    getInspectionStatisticsDetailPage,
  } from '@/api/inspectionStatistics';
  import { offlineExport } from '@/utils';

  export default {
    name: 'InspectionStatisticsList',
    data() {
      return {
        tableData: [],
        pageInfo: { current: 1, size: 10, total: 0 },
        searchParams: {
          listCode: '',
          listName: '',
          createTimeStart: '',
          createTimeEnd: '',
        },
        // 详情弹窗
        detailVisible: false,
        detailLoading: false,
        detailData: null,
        detailId: null,
        // 详情明细表格
        detailTableLoading: false,
        detailTableData: [],
        detailPageInfo: { current: 1, size: 10, total: 0 },
        detailSearchParams: { stationName: '' },
      };
    },
    computed: {
      searchData() {
        return [
          {
            comName: 'YkcInput',
            key: 'listCode',
            label: '清单编码',
            placeholder: '请输入清单编码',
          },
          {
            comName: 'YkcInput',
            key: 'listName',
            label: '清单名称',
            placeholder: '请输入清单名称',
          },
          {
            comName: 'YkcDatePicker',
            key: 'createTimeRange',
            label: '创建时间',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
            type: 'daterange',
            valueFormat: 'yyyy-MM-dd',
          },
        ];
      },
      tableColumns() {
        return [
          { label: '清单编码', prop: 'listCode', minWidth: '160px' },
          { label: '清单名称', prop: 'listName', minWidth: '180px' },
          { label: '周期开始时间', prop: 'cycleStartDate', minWidth: '150px' },
          { label: '周期结束时间', prop: 'cycleEndDate', minWidth: '150px' },
          { label: '创建时间', prop: 'createTime', minWidth: '160px' },
        ];
      },
      tableOperateButtons() {
        return [
          {
            id: '1',
            text: '查看',
            handleClick: (btn, { row }) => {
              this.handleDetail(row);
            },
          },
          {
            id: '2',
            text: '导出',
            handleClick: (btn, { row }) => {
              this.handleExport(row);
            },
          },
        ];
      },
    },
    created() {
      this.getList();
    },
    methods: {
      /** 查询列表 */
      getList() {
        getInspectionStatisticsPage({
          listCode: this.searchParams.listCode,
          listName: this.searchParams.listName,
          createTimeStart: this.searchParams.createTimeStart,
          createTimeEnd: this.searchParams.createTimeEnd,
          current: this.pageInfo.current,
          size: this.pageInfo.size,
        }).then(res => {
          this.tableData = (res && res.records) || [];
          this.pageInfo.total = (res && res.total) || 0;
        });
      },
      /** 搜索确认 */
      handleSearchConfirm(data) {
        if (data) {
          this.searchParams.listCode = data.listCode || '';
          this.searchParams.listName = data.listName || '';
          if (data.createTimeRange && data.createTimeRange.length === 2) {
            this.searchParams.createTimeStart = data.createTimeRange[0];
            this.searchParams.createTimeEnd = data.createTimeRange[1];
          } else {
            this.searchParams.createTimeStart = '';
            this.searchParams.createTimeEnd = '';
          }
        }
        this.pageInfo.current = 1;
        this.getList();
      },
      /** 搜索重置 */
      handleSearchClean() {
        this.searchParams = {
          listCode: '',
          listName: '',
          createTimeStart: '',
          createTimeEnd: '',
        };
        this.pageInfo.current = 1;
        this.getList();
      },
      /** 分页 */
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.getList();
      },
      handlePaginationSizeChange(size) {
        this.pageInfo.size = size;
        this.pageInfo.current = 1;
        this.getList();
      },
      /** 查看详情 */
      handleDetail(row) {
        this.detailId = row.id;
        this.detailVisible = true;
        this.detailLoading = true;
        this.detailData = null;
        this.detailSearchParams.stationName = '';
        this.detailPageInfo = { current: 1, size: 10, total: 0 };
        this.detailTableData = [];
        this.loadDetail();
        this.loadDetailPage();
      },
      /** 加载清单基础信息 */
      loadDetail() {
        getInspectionStatisticsDetail({ id: this.detailId })
          .then(res => {
            this.detailData = res;
          })
          .catch(err => {
            console.error('获取巡检统计清单详情失败', err);
            this.detailData = null;
          })
          .finally(() => {
            this.detailLoading = false;
          });
      },
      /** 加载清单明细分页 */
      loadDetailPage() {
        this.detailTableLoading = true;
        getInspectionStatisticsDetailPage({
          listId: this.detailId,
          stationName: this.detailSearchParams.stationName,
          current: this.detailPageInfo.current,
          size: this.detailPageInfo.size,
        })
          .then(res => {
            this.detailTableData = (res && res.records) || [];
            this.detailPageInfo.total = (res && res.total) || 0;
          })
          .catch(err => {
            console.error('获取巡检统计清单明细失败', err);
            this.detailTableData = [];
            this.detailPageInfo.total = 0;
          })
          .finally(() => {
            this.detailTableLoading = false;
          });
      },
      /** 明细电站名称筛选 */
      handleDetailSearch() {
        this.detailPageInfo.current = 1;
        this.loadDetailPage();
      },
      /** 明细分页 */
      handleDetailCurrentChange(current) {
        this.detailPageInfo.current = current;
        this.loadDetailPage();
      },
      handleDetailSizeChange(size) {
        this.detailPageInfo.size = size;
        this.detailPageInfo.current = 1;
        this.loadDetailPage();
      },
      /** 所属平台格式化 */
      formatPlatform(row) {
        const map = { 1: '益虫', 2: '淘淘充' };
        const val = row.platformName || row.platform;
        return map[val] || val || '--';
      },
      /** 列表行导出（导出指定清单的明细） */
      handleExport(row) {
        offlineExport({
          downloadType: 'inspection_statistics_export',
          jsonNode: { listId: row.id },
        });
      },
      /** 明细导出（按当前电站名称筛选导出当前清单明细） */
      handleDetailExport() {
        offlineExport({
          downloadType: 'inspection_statistics_export',
          jsonNode: {
            listId: this.detailId,
            stationName: this.detailSearchParams.stationName,
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .inspection-statistics-detail-dialog {
    .detail-loading,
    .detail-empty {
      text-align: center;
      padding: 40px 0;
      color: #9ca3af;
      font-size: 14px;
    }

    .detail-content {
      max-height: 65vh;
      overflow-y: auto;
    }

    .detail-section {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .detail-section-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 12px;
      padding-left: 10px;
      border-left: 3px solid #4d8cfd;
    }

    .detail-info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px 24px;
    }

    .detail-info-item {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 1.6;
    }

    .detail-info-label {
      color: #6b7280;
      width: 110px;
      flex-shrink: 0;
    }

    .detail-info-value {
      color: #1f2937;
    }

    .detail-toolbar {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      .detail-toolbar-input {
        width: 220px;
      }
    }

    .detail-items-table {
      width: 100%;
    }

    .detail-pagination {
      margin-top: 12px;
      text-align: right;
    }

    .status-done {
      color: #38a169;
      font-weight: 500;
    }

    .status-undo {
      color: #909399;
      font-weight: 500;
    }
  }
</style>
